<template>
    <page-shell
        title="Contacts"
        subtitle="Here you can create contacts for your location(s). These contacts receive our location newsletters. If you flag them for monitoring, we also send messages when there are technical issues with your X-Cube."
    >
        <div class="contacts-toolbar">
            <v-select
                v-model="locationFilter"
                :options="locationFilterOptions"
                :reduce="optionId"
                label="name"
                :clearable="false"
                placeholder="All locations"
                @input="reloadList"
            />
            <el-input v-model="searchQuery" clearable placeholder="Search" class="contacts-search" />
        </div>

        <div v-if="isNew && form" class="contacts-editor contacts-editor--new">
            <div class="contacts-editor-header">
                <h2>New contact</h2>
                <el-button type="text" @click="closeEditor">Cancel</el-button>
            </div>
            <contact-form
                ref="contactFormRef"
                :form="form"
                :locations="pickerLocations"
                :function-options="functionOptions"
                :language-options="languageOptions"
                :can-unset-monitoring="canUnsetMonitoring"
                @update:form="form = $event"
            />
            <div class="contacts-actions">
                <el-button type="primary" :loading="saving" @click="save">Save</el-button>
            </div>
        </div>

        <div class="contacts-list">
            <el-table
                v-if="!loadingList"
                :data="filteredContacts"
                row-key="id"
                empty-text="No data"
                :expand-row-keys="expandedRowKeys"
                :row-class-name="rowClassName"
                highlight-current-row
                @row-click="onRowClick"
                @expand-change="onExpandChange"
            >
                <el-table-column type="expand" width="40">
                    <template slot-scope="scope">
                        <div
                            v-if="!isNew && selectedId === scope.row.id"
                            class="contacts-editor contacts-editor--expand"
                            @click.stop
                        >
                            <div v-if="loadingDetail" class="contacts-loading">
                                <i class="el-icon-loading" />
                                <span>Loading contact...</span>
                            </div>
                            <template v-else-if="form && isEditing">
                                <div class="contacts-editor-header">
                                    <h2>Edit contact</h2>
                                    <el-button type="text" @click="cancelEdit">Cancel</el-button>
                                </div>
                                <contact-form
                                    ref="contactFormRef"
                                    :form="form"
                                    :locations="pickerLocations"
                                    :function-options="functionOptions"
                                    :language-options="languageOptions"
                                    :can-unset-monitoring="canUnsetMonitoring"
                                    @update:form="form = $event"
                                />
                                <div class="contacts-actions">
                                    <el-button type="primary" :loading="saving" @click="save">
                                        Save
                                    </el-button>
                                    <el-button :loading="deactivating" @click="confirmDeactivate">
                                        Deactivate
                                    </el-button>
                                </div>
                            </template>
                            <template v-else-if="form">
                                <div class="contacts-editor-header">
                                    <h2>Contact</h2>
                                </div>
                                <contact-detail
                                    :form="form"
                                    :location-names="selectedLocationNames"
                                    :language-options="languageOptions"
                                />
                                <div class="contacts-actions">
                                    <el-button type="primary" @click="isEditing = true">
                                        Edit
                                    </el-button>
                                    <el-button @click="closeEditor">Close</el-button>
                                </div>
                            </template>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="Name" min-width="160">
                    <template slot-scope="scope">
                        {{ formatContactName(scope.row) }}
                    </template>
                </el-table-column>
                <el-table-column prop="emailadres" label="Email" min-width="150" />
                <el-table-column label="Phone" min-width="130">
                    <template slot-scope="scope">
                        {{ formatContactPhone(scope.row) }}
                    </template>
                </el-table-column>
                <el-table-column label="Location" min-width="220">
                    <template slot-scope="scope">
                        <span :title="formatContactLocationsTitle(scope.row.locationNames)">
                            {{ formatContactLocations(scope.row.locationNames) }}
                        </span>
                    </template>
                </el-table-column>
                <el-table-column label="Monitoring" width="120">
                    <template slot-scope="scope">
                        <el-tag v-if="scope.row.receive_monitoring" disable-transitions>
                            Monitoring
                        </el-tag>
                    </template>
                </el-table-column>
            </el-table>
            <spinner :loading="loadingList" text="Loading contacts..." />
        </div>
        <el-button
            v-if="!isNew"
            class="create-contact-button"
            type="primary"
            @click="startNew"
        >
            New contact
        </el-button>
    </page-shell>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from "vue";
import { Message, MessageBox } from "element-ui";
import {
    createContact,
    deactivateContact,
    emptyContactForm,
    getContact,
    listContacts,
    updateContact,
} from "../../api/contacts";
import { fetchUserData } from "../../api/graphql/users";
import {
    formatContactLocations,
    formatContactLocationsTitle,
} from "../../helpers/contactLocations";
import { formatContactName, formatContactPhone } from "../../helpers/contactName";
import { normalizeLanguageOptions } from "../../helpers/contactLanguage";
import useCurrentUserStore from "../../store/userStore";
import ContactDetail from "../contacts/ContactDetail.vue";
import ContactForm from "../contacts/ContactForm.vue";
import PageShell from "../shared/PageShell.vue";
import Spinner from "../shared/Spinner.vue";

const store = useCurrentUserStore();
const contacts = ref([]);
const pickerLocations = ref([]);
const functionOptions = ref([]);
const languageOptions = ref([]);
const loadingList = ref(false);
const saving = ref(false);
const deactivating = ref(false);
const locationFilter = ref("all");
const searchQuery = ref("");
const selectedId = ref(null);
const isNew = ref(false);
const isEditing = ref(false);
const loadingDetail = ref(false);
const form = ref(null);
const lastLoadedForm = ref(null);
const contactFormRef = ref(null);
const canUnsetMonitoring = ref(true);
let loadSeq = 0;

const hubLocations = computed(() => store.currentUser?.locations || []);

const locationFilterOptions = computed(() => [
    { id: "all", name: "All locations" },
    ...hubLocations.value.map((loc) => ({ id: String(loc.id), name: loc.name })),
]);

const optionId = (item) => item.id;

const expandedRowKeys = computed(() =>
    !isNew.value && selectedId.value ? [selectedId.value] : [],
);

const selectedLocationNames = computed(() => {
    if (!form.value) return [];
    const byId = new Map(
        pickerLocations.value.map((loc) => [String(loc.id), loc.name]),
    );
    const fromIds = (form.value.locationIds || [])
        .map((id) => byId.get(String(id)))
        .filter(Boolean);
    if (fromIds.length) return fromIds;
    const row = contacts.value.find((contact) => contact.id === selectedId.value);
    return row?.locationNames || [];
});

const rowClassName = ({ row }) =>
    selectedId.value === row.id && !isNew.value ? "contacts-row--open" : "";

const filteredContacts = computed(() => {
    const q = searchQuery.value.trim().toLowerCase();
    if (!q) return contacts.value;
    return contacts.value.filter((contact) => {
        const hay = [
            contact.label,
            contact.first_name,
            contact.last_name,
            contact.emailadres,
            contact.telephone_mobile,
            contact.telephone_location,
            contact.function,
            ...(contact.locationNames || []),
        ]
            .join(" ")
            .toLowerCase();
        return hay.includes(q);
    });
});

const showError = (error) => {
    Message.error({ message: error?.message || "Something went wrong" });
};

const reloadList = async () => {
    loadingList.value = true;
    try {
        const data = await listContacts({
            locationId: locationFilter.value,
        });
        contacts.value = data.contacts || [];
        pickerLocations.value = data.locations || [];
        functionOptions.value = data.fieldOptions?.function || [];
        languageOptions.value = normalizeLanguageOptions(
            data.fieldOptions?.language,
        );
    } catch (error) {
        showError(error);
    } finally {
        loadingList.value = false;
    }
};

const snapshotForm = (next) => JSON.parse(JSON.stringify(next));

const closeEditor = () => {
    loadSeq += 1;
    form.value = null;
    lastLoadedForm.value = null;
    selectedId.value = null;
    isNew.value = false;
    isEditing.value = false;
    loadingDetail.value = false;
};

const cancelEdit = () => {
    if (lastLoadedForm.value) {
        form.value = snapshotForm(lastLoadedForm.value);
    }
    isEditing.value = false;
};

const startNew = () => {
    isNew.value = true;
    isEditing.value = true;
    selectedId.value = null;
    loadingDetail.value = false;
    canUnsetMonitoring.value = true;
    const next = emptyContactForm();
    if (pickerLocations.value.length === 1) {
        next.locationIds = [pickerLocations.value[0].id];
    }
    form.value = next;
};

const loadContact = async (row) => {
    if (!row?.id) return;
    const seq = ++loadSeq;
    isNew.value = false;
    isEditing.value = false;
    selectedId.value = row.id;
    form.value = null;
    loadingDetail.value = true;
    try {
        const data = await getContact(row.id);
        if (seq !== loadSeq) return;
        form.value = data.form;
        lastLoadedForm.value = snapshotForm(data.form);
        canUnsetMonitoring.value = data.can_unset_monitoring !== false;
        if (data.locations) pickerLocations.value = data.locations;
        if (data.fieldOptions) {
            functionOptions.value = data.fieldOptions.function || functionOptions.value;
            languageOptions.value = normalizeLanguageOptions(
                data.fieldOptions.language || languageOptions.value,
            );
        }
        await nextTick();
    } catch (error) {
        if (seq !== loadSeq) return;
        showError(error);
        closeEditor();
    } finally {
        if (seq === loadSeq) loadingDetail.value = false;
    }
};

const onRowClick = async (row, _column, event) => {
    if (!row?.id) return;
    if (event?.target?.closest?.(".el-table__expand-icon")) return;
    if (!isNew.value && selectedId.value === row.id) return;
    await loadContact(row);
};

const onExpandChange = async (row, expandedRows) => {
    const open = expandedRows.some((r) => r.id === row.id);
    if (!open) {
        if (selectedId.value === row.id) closeEditor();
        return;
    }
    if (selectedId.value !== row.id || isNew.value) {
        await loadContact(row);
    }
};

const save = async () => {
    if (!form.value || !contactFormRef.value) return;
    try {
        await contactFormRef.value.validate();
    } catch {
        return;
    }
    saving.value = true;
    try {
        const creating = isNew.value;
        const result = creating
            ? await createContact(form.value)
            : await updateContact(selectedId.value, form.value);
        selectedId.value = result.recordId;
        isNew.value = false;
        isEditing.value = false;
        form.value = { ...form.value, recordId: result.recordId };
        lastLoadedForm.value = snapshotForm(form.value);
        if (typeof result.can_unset_monitoring === "boolean") {
            canUnsetMonitoring.value = result.can_unset_monitoring;
        }
        Message.success({
            message: creating ? "Contact created" : "Contact saved",
        });
        await reloadList();
    } catch (error) {
        showError(error);
    } finally {
        saving.value = false;
    }
};

const confirmDeactivate = async () => {
    if (!selectedId.value) return;
    try {
        await MessageBox.confirm(
            "This contact will be set inactive. It will not be deleted.",
            "Deactivate contact",
            { confirmButtonText: "Deactivate", cancelButtonText: "Cancel" },
        );
    } catch {
        // Element UI rejects the promise when the user cancels.
        return;
    }
    deactivating.value = true;
    try {
        await deactivateContact(selectedId.value);
        Message.success({ message: "Contact deactivated" });
        closeEditor();
        await reloadList();
    } catch (error) {
        showError(error);
    } finally {
        deactivating.value = false;
    }
};

onMounted(async () => {
    if (!store.currentUser?.locations) {
        await fetchUserData();
    }
    await reloadList();
});
</script>

<style scoped lang="scss">
@use "../../colors" as *;

.create-contact-button {
    margin-top: 1rem;
}

.contacts-toolbar {
    display: flex;
    gap: 1rem;
    margin: 0 0 1rem;
    max-width: 40rem;

    .v-select {
        flex: 1;
        min-width: 0;
    }
}

.contacts-search {
    flex: 1;
}

.contacts-list {
    min-width: 0;

    .el-table {
        cursor: pointer;
    }
}

.contacts-loading {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.35rem 0;
    opacity: 0.85;
}

:deep(.el-table__expand-icon) {
    color: $color-text-regular;
}

:deep(.el-table .contacts-row--open) {
    background: rgba($color-background, 0.35);
}

.contacts-editor {
    background: rgba($color-background, 0.55);
    padding: 1rem 1.25rem 1.25rem;
    border: 1px solid $color-primary;
    border-radius: 0.5rem;
    margin-bottom: 1rem;
}

.contacts-editor--expand {
    margin: 0.5rem 0 0.75rem;
}

.contacts-editor-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.5rem;

    h2 {
        margin: 0;
    }
}

.contacts-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-top: 1rem;
}

:deep(.el-table__expanded-cell) {
    background: transparent;
    padding: 0.75rem 1rem 1rem;
}
</style>
